'use client';

import { useMemo } from 'react';
import { TopBanner, FaqBanner, Faqs, HighlightedCTA } from '@/app/components/shared';
import { AppSupport, Header as HeaderVideo, Footer } from '@/app/_components/platform';
import { ClientProvider } from '@/contexts/client.context';
import DynamicBody from '@/components/core/DynamicBody';
import CoveredModal from '@/app/components/core/CoveredModal';
import { extractFieldsFromTemplate, mergeObjects } from '@/utils/merge-objects';
import usePersonalization from '@/utils/usePersonalization';
import DynamicHeader from '@/app/components/platform/DynamicHeader';
import { getEligibilityClaim } from '@/utils/eligibility-claim';

const PlatformClient = ({
  content: client,
  product,
  personalization,
  referral = false,
  userName,
  scrollToTop,
}) => {
  const {
    clientKey,
    institutionKey,
    logo,
    header: clientHeader,
    template,
    launchDate,
    showSwordLogo,
    comingSoonHref,
    countries,
    country,
    isActive,
  } = client;

  usePersonalization(personalization);

  // Safely extract template fields.
  const extractedTemplate = extractFieldsFromTemplate(template) || {};
  const {
    header: templateHeader,
    heroHeader: templateHeroHeader,
    ...templateFields
  } = extractedTemplate;

  // override template variables
  const mergedHeader = useMemo(() => {
    if (!clientHeader && !templateHeader) return null;
    return {
      ...mergeObjects(templateHeader, clientHeader),
      logo: logo?.data,
      video: clientHeader?.video?.data ? clientHeader?.video : templateHeader?.video,
      previewVideo: clientHeader?.previewVideo?.data
        ? clientHeader?.previewVideo
        : templateHeader?.previewVideo,
    };
  }, [templateHeader, clientHeader, logo]);

  const mergedHeroHeader = useMemo(() => {
    if (!client?.heroHeader?.[0] && !templateHeroHeader?.[0]) return null;
    return {
      ...mergeObjects(templateHeroHeader?.[0], client?.heroHeader?.[0]),
      content: mergeObjects(templateHeroHeader?.[0]?.content, client?.heroHeader?.[0]?.content),
    };
  }, [templateHeroHeader, client.heroHeader]);

  const eligibility = getEligibilityClaim(client);

  if (mergedHeroHeader?.content) {
    mergedHeroHeader.content.eligibilityClaim = eligibility;
  }

  const renderHeader = () => {
    if (referral) return null;

    if (mergedHeroHeader?.content) {
      return (
        <DynamicHeader
          content={mergedHeroHeader}
          clientKey={clientKey}
          topBanner={!!topBanner && topBanner?.active}
          logo={logo?.data}
          showSwordLogo={showSwordLogo}
          userName={userName}
        />
      );
    }

    if (mergedHeader) {
      return (
        <HeaderVideo
          content={mergedHeader}
          logo={logo}
          showSwordLogo={showSwordLogo}
          userName={userName}
        />
      );
    }

    return null;
  };

  const app = useMemo(() => {
    if (!templateFields?.app) return null;
    return {
      ...templateFields.app,
      active: client.showAppSection ?? templateFields.app.active,
    };
  }, [templateFields?.app, client.showAppSection]);
  const contact = useMemo(
    () => mergeObjects(templateFields?.contact, client?.contact),
    [templateFields?.contact, client?.contact],
  );
  const disclaimer = client.disclaimer ?? templateFields?.disclaimer;
  const faqs = useMemo(
    () => mergeObjects(templateFields?.faqs, client?.faqs),
    [templateFields?.faqs, client?.faqs],
  );
  const faqBanner = useMemo(
    () => mergeObjects(templateFields?.faqBanner, client?.faqBanner),
    [templateFields?.faqBanner, client?.faqBanner],
  );
  const footnotes = client?.footnotes?.length ? client.footnotes : templateFields?.footnotes;
  const topBanner = useMemo(
    () => mergeObjects(templateFields?.topBanner, client?.banner),
    [templateFields?.topBanner, client?.banner],
  );

  return (
    <>
      <ClientProvider
        defaultClient={{
          clientKey,
          institutionKey,
          launchDate,
          isActive,
          product,
          countries,
          country,
          comingSoonHref,
        }}
      >
        {!referral && templateFields?.contact && topBanner && (
          <TopBanner {...contact} {...topBanner} />
        )}
        <CoveredModal />
        {renderHeader()}
        {templateFields && (
          <DynamicBody
            body={templateFields.dynamicPlaceholder}
            client={client}
            scrollTop={scrollToTop}
          />
        )}
        {templateFields?.cta && <HighlightedCTA content={templateFields.cta} />}
        {faqs && <Faqs content={faqs} />}
        {faqBanner && contact && <FaqBanner content={faqBanner} contact={contact} />}
        {app && <AppSupport content={app} />}
        {!referral && (
          <Footer
            bottomCTA={templateFields?.bottomCTA}
            disclaimer={disclaimer}
            footnotes={footnotes}
            email={contact?.email}
          />
        )}
      </ClientProvider>
    </>
  );
};
export default PlatformClient;
